import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { getEicrPresets, saveEicrPresets } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';

const CODES = ['C1', 'C2', 'C3', 'FI'];
const CODE_STYLES = {
  C1: 'bg-rose-100 text-rose-800 border-rose-200',
  C2: 'bg-orange-100 text-orange-800 border-orange-200',
  C3: 'bg-amber-100 text-amber-800 border-amber-200',
  FI: 'bg-rose-100 text-rose-800 border-rose-200',
};

const EicrPresetsEditor = () => {
  const location = useLocation();
  const base = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  const [presets, setPresets] = useState({ C1: [], C2: [], C3: [], FI: [] });
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getEicrPresets()
      .then((res) => {
        setPresets(res.presets || { C1: [], C2: [], C3: [], FI: [] });
        setLabels(res.labels || {});
      })
      .catch(() => toast.error('Failed to load presets'))
      .finally(() => setLoading(false));
  }, []);

  const addRow = (code) => {
    setPresets((p) => ({ ...p, [code]: [...(p[code] || []), ''] }));
  };
  const patchRow = (code, idx, value) => {
    setPresets((p) => ({
      ...p,
      [code]: p[code].map((r, i) => (i === idx ? value : r)),
    }));
  };
  const removeRow = (code, idx) => {
    setPresets((p) => ({ ...p, [code]: p[code].filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(CODES.map((c) => [c, (presets[c] || []).map((r) => r.trim()).filter(Boolean)]));
      await saveEicrPresets(cleaned);
      setPresets(cleaned);
      toast.success('Presets saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-3xl">
      <PageHeader
        title="EICR Reason Presets"
        description="Edit the drop-down list of reasons the engineer can pick when logging an observation. Custom text is still allowed on the finding — this list is just to speed up common cases."
        actions={
          <div className="flex gap-2 items-center">
            <Link to={`${base}/eicr-reports`}>
              <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            </Link>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="eicr-presets-save"
            >
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      />

      <div className="space-y-4">
        {CODES.map((code) => (
          <div key={code} className="bg-white border border-slate-200 rounded-md p-4 space-y-2" data-testid={`eicr-presets-${code}`}>
            <div className="flex items-center justify-between">
              <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded border ${CODE_STYLES[code]}`}>
                {labels[code] || code}
              </span>
              <Button size="sm" variant="outline" onClick={() => addRow(code)} data-testid={`eicr-presets-${code}-add`}>
                <Plus className="h-3 w-3 mr-1" /> Add reason
              </Button>
            </div>
            <div className="space-y-1.5">
              {(presets[code] || []).map((row, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input
                    value={row}
                    onChange={(e) => patchRow(code, i, e.target.value)}
                    className="flex-1"
                    data-testid={`eicr-presets-${code}-input-${i}`}
                  />
                  <button
                    onClick={() => removeRow(code, i)}
                    className="text-slate-400 hover:text-rose-600 shrink-0"
                    aria-label="Remove"
                    data-testid={`eicr-presets-${code}-remove-${i}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {(presets[code] || []).length === 0 && (
                <div className="text-xs text-slate-400 italic px-1">No presets — engineers will only be able to type custom text.</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EicrPresetsEditor;
